// YNX AUTH — Emergent-managed Google OAuth client
// ───────────────────────────────────────────────────────────────
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH
// ───────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = 'ynx_auth_token';

export const getAuthToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; }
  catch (e) { console.debug('YNX auth: localStorage read failed', e); return ''; }
};

export const setAuthToken = (token) => {
  try { localStorage.setItem(TOKEN_KEY, token || ''); }
  catch (e) { console.debug('YNX auth: localStorage write failed', e); }
};

export const clearAuthToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); }
  catch (e) { console.debug('YNX auth: localStorage clear failed', e); }
};

export const authHeaders = (extra = {}) => {
  const t = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
    ...extra,
  };
};

const json = async (res) => {
  if (!res.ok) {
    let err = `HTTP ${res.status}`;
    try { const j = await res.json(); err = j.detail || err; }
    catch (e) { console.debug('YNX auth: JSON parse failed, using status', e); }
    throw new Error(err);
  }
  return res.json();
};

// ─── Public API ───────────────────────────────────────────────
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/?auth=1';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const exchangeSession = async (session_id) => {
  const res = await fetch(`${API}/api/auth/session`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id }),
  });
  const data = await json(res);
  if (data.session_token) setAuthToken(data.session_token);
  return data;
};

export const getMe = async () => {
  const res = await fetch(`${API}/api/auth/me`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  return json(res);
};

export const logout = async () => {
  try {
    await fetch(`${API}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
    });
  } catch (e) { console.debug('YNX auth: logout request failed (continuing)', e); }
  clearAuthToken();
};

// ─── Workspace member helpers (Google-authenticated) ─────────
export const claimAdmin = (ws_id) =>
  fetch(`${API}/api/biz/workspace/${ws_id}/claim-admin`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
  }).then(json);

export const joinAsUser = (code) =>
  fetch(`${API}/api/biz/workspace/join-as-user`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ code }),
  }).then(json);

export const listMyWorkspaces = () =>
  fetch(`${API}/api/biz/my/workspaces`, {
    credentials: 'include',
    headers: authHeaders(),
  }).then(json);

const memberHeaders = (extra = {}) => {
  let wsId = '';
  try { wsId = localStorage.getItem('ynx_workspace_id') || ''; }
  catch (e) { console.debug('YNX auth: workspace lookup failed', e); }
  return {
    ...authHeaders(extra),
    ...(wsId ? { 'X-YNX-Workspace': wsId } : {}),
  };
};

export const listMembers = () =>
  fetch(`${API}/api/biz/members`, {
    credentials: 'include',
    headers: memberHeaders(),
  }).then(json);

export const updateMember = (member_id, role) =>
  fetch(`${API}/api/biz/members/${member_id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: memberHeaders(),
    body: JSON.stringify({ role }),
  }).then(json);

export const removeMember = (member_id) =>
  fetch(`${API}/api/biz/members/${member_id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: memberHeaders(),
  }).then(json);
